import React, { useMemo, useState } from "react";
import { Box, Button, Chip, Stack, Typography, useTheme } from "@mui/material";
import { FormattedMessage, useIntl } from "react-intl";
import LinkIcon from "@mui/icons-material/Link";
import LinkOffIcon from "@mui/icons-material/LinkOff";
import AxionControls from "../../../components/controls/AxionControls";
import { canAssignMachine } from "../assetHierarchyUtils";
import { getAssetHierarchyPalette, getContainedButtonSx, getOutlinedButtonSx, getStatusChipSx } from "../assetHierarchyStyles";
import { HideIfNoRole } from "../../../auth/authUi";
import { CMMS_MANAGER_ROLES } from "../cmmsRoles";
import { useActiveToolServiceStatus } from "../../../hooks/useActiveToolServiceStatus";
import { MountedToolState, resolveMountedToolState } from "../toolServiceStatus";
import ToolServiceStatusCell from "./ToolServiceStatusCell";
import messages from "../messages";

export default function MachineAssignmentPanel({ linkedMachine, machineOptions, onAssignMachine, onUnassignMachine, selectedNode }) {
  const theme = useTheme();
  const intl = useIntl();
  const cmmsColors = getAssetHierarchyPalette(theme.palette.mode);
  const [machineId, setMachineId] = useState("");
  const canAssign = canAssignMachine(selectedNode);

  /*
   * What is in the linked machine right now.
   *
   * The hook is called unconditionally and steered by its argument; `null` trips its own
   * `enabled` and no request goes out. Gating it with an `if` would be a conditional hook, and
   * gating it with HideIfNoRole would not help at all — hooks run in the component that mounts,
   * not in the JSX that hides. Roles and the module switch are already inside the hook.
   *
   * Nothing is fetched until this tab is selected: AssetNodeDetailsPanel renders only the active
   * tab's render(), so an unopened Machine tab costs no requests.
   */
  const toolAssetNodeId = selectedNode?.nodeType === 3 && linkedMachine ? selectedNode.id : null;
  const {
    tool,
    status: toolStatus,
    isLoading: toolLoading,
    isError: toolFailed,
  } = useActiveToolServiceStatus(toolAssetNodeId);
  const mountedState = resolveMountedToolState(tool);

  const availableMachineOptions = useMemo(() => {
    return (machineOptions ?? [])
      .filter((machine) => !machine.assetNodeId)
      .map((machine) => ({
        id: machine.id,
        title: machine.machineName,
      }));
  }, [machineOptions]);

  const handleAssign = () => {
    if (!machineId) return;
    onAssignMachine(Number(machineId));
    setMachineId("");
  };

  return (
    <Box>
      <Typography variant="subtitle1" sx={{ color: cmmsColors.text, fontWeight: 700 }}>
        <FormattedMessage {...messages.machineLinkTitle} />
      </Typography>
      <Typography variant="body2" sx={{ color: cmmsColors.mutedText, fontSize: 14, mt: 0.5, mb: 2 }}>
        <FormattedMessage
          {...(selectedNode?.nodeType === 3 ? messages.machineLinkIntroObject : messages.machineLinkIntroOther)}
        />
      </Typography>

      {selectedNode?.nodeType !== 3 &&
        <Typography variant="body2" sx={{ color: cmmsColors.mutedText, fontSize: 14 }}>
          <FormattedMessage {...messages.machineLinkSelectObject} />
        </Typography>
      }

      {selectedNode?.nodeType === 3 && linkedMachine &&
        <Box
          sx={{
            display: "flex",
            alignItems: { xs: "flex-start", sm: "center" },
            justifyContent: "space-between",
            gap: 1.5,
            flexDirection: { xs: "column", sm: "row" },
            px: 1.5,
            py: 1.25,
            border: `1px solid ${cmmsColors.border}`,
            borderRadius: 0.50,
            backgroundColor: cmmsColors.surfaceHover,
          }}
        >
          <Box sx={{ minWidth: 0 }}>
            <Stack direction="row" spacing={1} alignItems="center" sx={{ flexWrap: "wrap" }}>
              <Typography variant="body2" sx={{ color: cmmsColors.text, fontSize: 14, fontWeight: 700 }}>
                {linkedMachine.machineName ?? intl.formatMessage(messages.machineLinkLinkedFallback)}
              </Typography>
              <Chip label={intl.formatMessage(messages.machineLinkLinkedChip)} size="small" sx={getStatusChipSx(cmmsColors, true)} />
            </Stack>
            <Typography variant="caption" sx={{ color: cmmsColors.mutedText, display: "block", mt: 0.5 }}>
              <FormattedMessage {...messages.machineLinkAlreadyMapped} />
            </Typography>
          </Box>
          {/* Which machine is linked stays readable for everyone; only the unlink action
              is manager-gated, matching the server policy. */}
          <HideIfNoRole anyOf={CMMS_MANAGER_ROLES}>
            <Button color="error" variant="outlined" size="small" startIcon={<LinkOffIcon />} onClick={() => onUnassignMachine(linkedMachine)} sx={getOutlinedButtonSx(cmmsColors, "danger")}>
              <FormattedMessage {...messages.machineLinkUnlink} />
            </Button>
          </HideIfNoRole>
        </Box>
      }

      {/*
        * The tool currently in that machine, read-only.
        *
        * The link this panel manages is the asset node's, and it is permanent. The one below is
        * the production order's, and it changes with every order — which is why it is read here
        * and never edited here: Machines.ActiveTool is ERP's contract, and a wrong value is a
        * fault in the production order, not something CMMS offers to override.
        *
        * Unlike the badge on the machine page, "nothing mounted" is stated rather than left
        * blank. That block is ambient on a busy tab, where silence is the polite default; this
        * one is the answer to a question the user asked by opening the tab, and withholding it
        * would read as a panel that failed to load.
        */}
      {selectedNode?.nodeType === 3 && linkedMachine &&
        <Box sx={{ mt: 2, maxWidth: 420 }}>
          <Typography variant="caption" sx={{ color: cmmsColors.mutedText, display: "block", fontWeight: 700 }}>
            <FormattedMessage {...messages.machineToolHeading} />
          </Typography>

          {/* Skeleton first: before the read lands, "no tool mounted" is not yet true, and
              flashing it on every open would be a wrong answer rather than a pending one. */}
          {toolLoading && <ToolServiceStatusCell isLoading />}

          {/* A failed read is said out loud rather than folded into "no tool mounted". The two
              look identical from here — both leave `tool` empty — but one is a fact about the
              machine and the other is a fact about us, and only the first is safe to act on. */}
          {!toolLoading && toolFailed &&
            <Typography variant="body2" sx={{ color: cmmsColors.mutedText, fontSize: 14, mt: 0.5 }}>
              <FormattedMessage {...messages.machineToolFailed} />
            </Typography>
          }

          {!toolLoading && !toolFailed && mountedState === MountedToolState.None &&
            <Typography variant="body2" sx={{ color: cmmsColors.mutedText, fontSize: 14, mt: 0.5 }}>
              <FormattedMessage {...messages.machineToolNone} />
            </Typography>
          }

          {/* The name already sits inside the message, so it is not repeated above it. */}
          {!toolLoading && !toolFailed && mountedState === MountedToolState.Unregistered &&
            <Typography variant="body2" sx={{ color: cmmsColors.mutedText, fontSize: 14, mt: 0.5, wordBreak: "break-word" }}>
              {intl.formatMessage(messages.machineToolUnmatched, { tool: tool.toolName })}
            </Typography>
          }

          {!toolLoading && !toolFailed && mountedState === MountedToolState.Registered &&
            <>
              <Typography variant="body2" sx={{ color: cmmsColors.text, fontSize: 14, fontWeight: 700, mt: 0.5, mb: 0.5, wordBreak: "break-word" }}>
                {tool.toolName}
              </Typography>
              {/* A null status is a reading in itself — the cell answers it with its dash and the
                  "nothing watches this" tooltip, which is the same thing /tools shows for the
                  majority of a 1 465-row register. */}
              <ToolServiceStatusCell status={toolStatus} />
            </>
          }
        </Box>
      }

      {selectedNode?.nodeType === 3 && !linkedMachine &&
        <HideIfNoRole anyOf={CMMS_MANAGER_ROLES}>
          <Box sx={{ mb: 2 }}>
            <Stack direction={{ xs: "column", sm: "row" }} spacing={1} alignItems={{ xs: "stretch", sm: "center" }}>
              <Box sx={{ minWidth: 260, flex: 1 }}>
                <AxionControls.AxionSelect
                  name="machineId"
                  label={intl.formatMessage(messages.machineLinkField)}
                  value={machineId}
                  onChange={(event) => setMachineId(event.target.value)}
                  options={availableMachineOptions}
                  searchField
                  disabled={!availableMachineOptions.length}
                />
              </Box>
              <Button variant="contained" startIcon={<LinkIcon />} onClick={handleAssign} disabled={!machineId || !canAssign} sx={getContainedButtonSx(cmmsColors)}>
                <FormattedMessage {...messages.machineLinkLink} />
              </Button>
            </Stack>
          </Box>
        </HideIfNoRole>
      }

      {selectedNode?.nodeType === 3 && !linkedMachine && !canAssign &&
        <Typography variant="body2" sx={{ color: cmmsColors.mutedText, fontSize: 14 }}>
          <FormattedMessage {...messages.machineLinkActivateFirst} />
        </Typography>
      }

      {selectedNode?.nodeType === 3 && !linkedMachine && canAssign && !availableMachineOptions.length &&
        <Typography variant="body2" sx={{ color: cmmsColors.mutedText, fontSize: 14 }}>
          <FormattedMessage {...messages.machineLinkNoneAvailable} />
        </Typography>
      }

      {selectedNode?.nodeType === 3 && !linkedMachine && canAssign && Boolean(availableMachineOptions.length) &&
        <Typography variant="body2" sx={{ color: cmmsColors.mutedText, fontSize: 14 }}>
          <FormattedMessage {...messages.machineLinkLeaveEmpty} />
        </Typography>
      }
    </Box>
  );
}
